import React, {useState, useEffect, useRef} from "react";

import {useObserver} from "mobx-react-lite";
import {useHistory} from "react-router-dom";
import {Col, Row, Tabs, Spin} from "antd";
import {useTranslation} from "react-i18next";
import {CompanyFilter, SearchText} from "../../_defaultComponents/FilterComponents";
import useIsFocused from "../../../hooks/useIsFocused";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import {deliveryStatuses} from "../../../constants/constants";
import {isConstantExist, isNumeric} from "../../../utils/helpers";
import {CustomTab, SelectField, TabRow} from "./CustomerOrdersPage.styles";
import {CustomerOrdersTable} from "./customerOrdersTable/CustomerOrdersTable";
import {CustomerFluidSubPageLayout} from "../../layouts/customerLayout/customerFluidSubPageLayout/CustomerFluidSubPageLayout";
import ActiveOrderIcon from "../../../public/images/active-order-icon.svg";
import WaitingOrderIcon from "../../../public/images/waiting-order-icon.svg";
import CanceledOrderIcon from "../../../public/images/canceled-order-icon.svg";
import DeliveredOrderIcon from "../../../public/images/delivered-order-icon.svg";
import {CustomerMenuHeader} from "components/layouts/customerLayout/components/customerMenuHeader";
import {TimeRangeFilter} from "../customerHomePage/orderStats/timeRangeFilter/TimeRangeFilter";
import {OrderStats} from "../customerHomePage/orderStats/OrderStats";

const CustomerOrdersPage = () => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["orderPage", "deliveryStatuses"]);
    const isFocused = useIsFocused();
    const [currentPage, setCurrentPage] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [call, setCall] = useState(false);
    const [field, setField] = useState({field: "", asc: true});
    const [sortField, setSortField] = useState({field: "", asc: true});
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [dateType, setDateType] = useState("daily");
    recallRef.current = recall;

    const [statsRecall, setStatsRecall] = useState(true);
    const statsRecallRef = useRef(statsRecall);
    statsRecallRef.current = statsRecall;

    const filters = stores.meStore.orderPage?.filters;
    const filterStartDate = filters.dateRange.length > 0 ? filters.dateRange[0] : undefined;
    const filterEndDate = filters.dateRange.length > 0 ? filters.dateRange[1] : undefined;

    // Stats verileri için fetch
    const [statsLoading, statsData] = useFetch({
        fetchFn: stores.statisticStore.getHistoricStatistics.bind(null, {
            period: dateType,
            city: "",
            startDate: startDate,
            endDate: endDate
        }),
        defaultDataValue: {},
        initialCall: true,
        dependencies: [statsRecall, dateType, startDate, endDate]
    });

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companies: filters.companies.join("."),
            //city: stores.meStore.map.filters.city,
            limit: 1000
        }),
        dependencies: [filters.companies /* stores.meStore.map.filters.city */],
        initialCall:
            stores.meStore.checkRole("isCompanyUser") ||
            stores.meStore.orderPage?.filters.companies?.length > 0
    });

    const [orderListLoading, orderList, setOrderList, setOrderListLoading] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            regionGroups: filters.regionGroups,
            companies: filters.companies,
            subcompanies: filters.selectedSubCompanies,
            //city: stores.meStore.map.filters.city,
            deliveryStatus: filters.orderType,
            searchText,
            sortField,
            deliveryStatusOverrides: filters.deliveryStatusOverrides,
            groups: filters.upperGroup,
            startDate: filterStartDate,
            endDate: filterEndDate
        }),
        dependencies: [
            currentPage,
            //stores.meStore.map.filters.city,
            filters.companies,
            filters.selectedSubCompanies,
            filters.regionGroups,
            searchText,
            call,
            filters.orderType,
            sortField,
            filters.deliveryStatusOverrides,
            filters.upperGroup,
            filterStartDate,
            filterEndDate
        ],
        initialCall: false,
        callbackFunc: data => setRecallList(data)
    });

    const [recallListLoading, recallList, setRecallList, setRecallListLoading] = useFetch({
        fetchFn: stores.orderStore.getOrderList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            regionGroups: filters.regionGroups,
            companies: filters.companies,
            subcompanies: filters.selectedSubCompanies,
            //city: stores.meStore.map.filters.city,
            deliveryStatus: filters.orderType,
            searchText,
            sortField,
            deliveryStatusOverrides: filters.deliveryStatusOverrides,
            groups: filters.upperGroup,
            startDate: filterStartDate,
            endDate: filterEndDate
        }),
        dependencies: [recall],
        initialCall: false
    });

    useEffect(() => {
        if (isFocused) {
            let orderTimeout = null;
            let statsTimeout = null;

            if (
                filters.orderType === "current" ||
                filters.orderType === "burningOrders" ||
                filters.orderType === "inQueue" ||
                filters.orderType === "inSlot" ||
                filters.orderType === "inPrep"
            ) {
                orderTimeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 5000);
            }

            // Stats için de periyodik güncelleme
            statsTimeout = setTimeout(() => {
                setStatsRecall(!statsRecallRef.current);
            }, 10000); // 10 saniyede bir güncelle

            return () => {
                if (orderTimeout) clearTimeout(orderTimeout);
                if (statsTimeout) clearTimeout(statsTimeout);
            };
        }
    }, [recall, statsRecall, filters.orderType, isFocused]);

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPage = urlSearchParams.get("page");

        //let urlCity = urlSearchParams.get("city");
        let urlRegionGroups = urlSearchParams.get("regionGroups");
        let urlCompanies = urlSearchParams.get("companies");
        const urlSearchText = urlSearchParams.get("searchText");
        const urlOrderType = urlSearchParams.get("orderType");
        const urlDeliveryStatus = urlSearchParams.get("deliveryStatus");

        if (urlPage) {
            const page = parseInt(urlPage);
            if (!isNaN(page)) setCurrentPage(page);
        }

        /* if (urlCity) {
            const notValid = !isConstantExist(cities, urlCity);
            if (!notValid) {
                stores.meStore.map.filters.city = urlCity;
            }
        } */

        if (urlRegionGroups) {
            urlRegionGroups = urlRegionGroups.split("-");
            const notValid = urlRegionGroups.find(
                reg => !isConstantExist(filters.regionGroups, reg)
            );
            if (!notValid) {
                stores.meStore.orderPage.filters.regionGroups = urlRegionGroups;
            }
        }
        if (urlCompanies) {
            urlCompanies = urlCompanies.split("-");
            stores.meStore.orderPage.filters.companies = urlCompanies;
        }

        if (urlSearchText) {
            setSearchText(urlSearchText);
            setText(urlSearchText);
        }
        if (urlOrderType) {
            stores.meStore.orderPage.filters.orderType = urlOrderType;
        }
        if (urlDeliveryStatus) {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = [
                ...stores.meStore.orderPage.filters.deliveryStatusOverrides,
                urlDeliveryStatus
            ];
        }
    }, []);

    useEffect(() => {
        setSortField(field);
        setUrl();
    }, [
        currentPage,
        //stores.meStore.map.filters.city,
        filters.regionGroups,
        filters.companies,
        searchText,
        filters.orderType,
        field.asc,
        filters.deliveryStatusOverrides
    ]);

    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        /* if (stores.meStore.map.filters.city !== "") {
            path += "&city=" + stores.meStore.map.filters.city;
        } */
        if (filters.regionGroups.length) {
            path += "&regionsGroups=" + filters.regionGroups?.join("-");
        }
        if (filters.companies.length) {
            path += "&companies=" + filters.companies?.join("-");
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }
        if (filters.orderType) {
            path += "&orderType=" + filters.orderType;
        }
        if (filters.orderType) {
            path += "&orderType=" + filters.orderType;
        }

        history.push(path);
    };
    const changeTab = value => {
        setCurrentPage(1);

        if (value === "cancelled") {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = ["canceled"];
            stores.meStore.orderPage.filters.orderType = "past";
        } else {
            stores.meStore.orderPage.filters.deliveryStatusOverrides = [];
            stores.meStore.orderPage.filters.orderType = value;
        }
        stores.meStore.orderPage.filters.regionGroups = [];
        stores.meStore.orderPage.filters.upperGroup = "";
        stores.meStore.orderPage.filters.dateRange = [];
    };

    const tabIsActive = tab => {
        if (tab === "cancelled") {
            return (
                filters.orderType === "past" &&
                filters.deliveryStatusOverrides.includes("canceled") &&
                filters.deliveryStatusOverrides.length === 1
            );
        } else if (tab === "past") {
            return (
                filters.orderType === "past" &&
                !filters.deliveryStatusOverrides.includes("cancelled") &&
                filters.deliveryStatusOverrides.length !== 1
            );
        }

        return tab === filters.orderType;
    };

    // Stats render fonksiyonları
    const nullObjectCheck = obj => {
        return JSON.stringify(obj) === JSON.stringify({});
    };

    const renderActiveOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.onTheWayDeliveryCount)
        ) {
            return "";
        }
        return statsData?.onTheWayDeliveryCount;
    };

    const renderWaitingOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.inQueueDeliveryCount)
        ) {
            return "";
        }
        return statsData?.inQueueDeliveryCount;
    };

    const renderCanceledOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.canceledDeliveryCount)
        ) {
            return "";
        }
        return statsData?.canceledDeliveryCount;
    };

    const renderDeliveredOrderCount = () => {
        if (
            statsLoading ||
            nullObjectCheck(statsData) ||
            !isNumeric(statsData?.deliveredDeliveryCount)
        ) {
            return "";
        }
        return statsData?.deliveredDeliveryCount;
    };

    return useObserver(() => (
        <CustomerFluidSubPageLayout>
            <CustomerMenuHeader />
            {/* <OrderPage /> */}

            {/* ================================================== */}
            {/* Orders Tabs & Filters */}
            <Row type="flex" justify="center" data-testid="filterCard" style={{width: "100%"}}>
                {/* ================================================== */}
                {/* Orders Tabs */}
                <TabRow>
                    <CustomTab
                        key="current"
                        isActive={tabIsActive("current")}
                        onClick={() => changeTab("current")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        <div>
                            <div>{renderActiveOrderCount()}</div>
                            <div>Aktif</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="inQueue"
                        isActive={tabIsActive("inQueue")}
                        onClick={() => changeTab("inQueue")}
                    >
                        <img src={WaitingOrderIcon} alt="Waiting Order Icon" />
                        <div>
                            <div>{renderWaitingOrderCount()}</div>
                            <div>Bekleyen</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="cancelled"
                        isActive={tabIsActive("cancelled")}
                        onClick={() => {
                            changeTab("cancelled");
                        }}
                    >
                        <img src={CanceledOrderIcon} alt="Canceled Order Icon" />
                        <div>
                            <div>{renderCanceledOrderCount()}</div>
                            <div>İptal</div>
                        </div>
                    </CustomTab>
                    <CustomTab
                        key="past"
                        isActive={tabIsActive("past")}
                        onClick={() => changeTab("past")}
                    >
                        <img src={DeliveredOrderIcon} alt="Delivered Order Icon" />
                        <div>
                            <div>{renderDeliveredOrderCount()}</div>
                            <div>Tamamlanmış</div>
                        </div>
                    </CustomTab>

                    {/* <CustomTab
                        key="inSlot"
                        isActive={tabIsActive("inSlot")}
                        onClick={() => changeTab("inSlot")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        {t("filters.orderStatus.slotOrders", {ns: "orderPage"})}
                    </CustomTab> */}
                    {/* <CustomTab
                        key="inPrep"
                        isActive={tabIsActive("inPrep")}
                        onClick={() => changeTab("inPrep")}
                    >
                        <img src={ActiveOrderIcon} alt="Active Order Icon" />
                        {t("filters.orderStatus.inPreparationOrders", {ns: "orderPage"})}
                    </CustomTab> */}
                    {/* {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                        <CustomTab
                            key="burningOrders"
                            isActive={tabIsActive("burningOrders")}
                            onClick={() => changeTab("burningOrders")}
                        >
                            <img src={ActiveOrderIcon} alt="Active Order Icon" />
                            {t("filters.orderStatus.burningOrders", {ns: "orderPage"})}
                        </CustomTab>
                    )} */}
                </TabRow>
                <Col>
                    <Tabs
                        activeKey={filters.orderType}
                        onChange={changeTab}
                        className="orderTabs"
                    ></Tabs>
                </Col>
                {/* ================================================== */}
                {/* Orders Filters */}
                <Col span={24}>
                    <Row type="flex" gutter={[20, 20]}>
                        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{padding: "0px"}}>
                            <TimeRangeFilter
                                dateType={dateType}
                                selectFieldOnChange={value => {
                                    setStartDate("");
                                    setEndDate("");
                                    setDateType(value);
                                    // Stats verilerini güncelle
                                    setStatsRecall(!statsRecallRef.current);
                                }}
                                rangePickerOnChange={formatString => {
                                    setStartDate(formatString[0]);
                                    setEndDate(formatString[1]);
                                    // Stats verilerini güncelle
                                    setStatsRecall(!statsRecallRef.current);
                                }}
                                isOrdersPage={true}
                            />
                        </Col>
                        {filters.orderType !== "inQueue" &&
                            filters.orderType !== "inSlot" &&
                            filters.orderType !== "inPrep" && (
                                <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                    <h5>{t("fields.deliveryStatus", {ns: "common"})}</h5>
                                    <SelectField
                                        value={filters.deliveryStatusOverrides}
                                        onChange={value => {
                                            setCurrentPage(1);
                                            stores.meStore.orderPage.filters.deliveryStatusOverrides = value;
                                        }}
                                        mode="multiple"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        data-testid="deliveryStatusSelect"
                                    >
                                        {deliveryStatuses.map(deliveryStatusObj => {
                                            if (deliveryStatusObj.type === filters.orderType) {
                                                return (
                                                    <SelectField.Option
                                                        key={deliveryStatusObj.value}
                                                        value={deliveryStatusObj.value}
                                                        data-testid="deliveryStatusSelect_option"
                                                    >
                                                        {t(
                                                            `${deliveryStatusObj.translation_key}.shortName`,
                                                            {
                                                                ns: "deliveryStatuses"
                                                            }
                                                        )}
                                                    </SelectField.Option>
                                                );
                                            }
                                        })}
                                    </SelectField>
                                </Col>
                            )}
                        {!stores.meStore.checkRole("isCompanyUser") && (
                            <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                <CompanyFilter
                                    showPool={false}
                                    value={filters.companies}
                                    onChange={value => {
                                        setCurrentPage(1);
                                        stores.meStore.orderPage.filters.companies = value;
                                        stores.meStore.orderPage.filters.selectedSubCompanies = [];
                                    }}
                                    mode="multiple"
                                    placeholder={t("filters.addFilter", {ns: "common"})}
                                />
                            </Col>
                        )}
                        <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                            <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                            <SelectField
                                allowClear
                                data-testid="subCompanySelect"
                                value={filters.selectedSubCompanies}
                                mode="multiple"
                                showSearch
                                filterOption={(input, option) =>
                                    option.props.children
                                        .toLowerCase()
                                        .indexOf(input.toLowerCase()) >= 0 ||
                                    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >=
                                        0
                                }
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                onChange={value => {
                                    filters.selectedSubCompanies = value;
                                }}
                            >
                                {subCompanies.rows &&
                                    subCompanies?.rows.map(subCompany => (
                                        <SelectField.Option
                                            key={subCompany._id}
                                            value={subCompany._id}
                                            data-testid="subCompanySelect_option"
                                        >
                                            {subCompany.name}
                                        </SelectField.Option>
                                    ))}
                            </SelectField>
                        </Col>
                        <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                            <SearchText
                                showLabel={true}
                                showPlaceholder={true}
                                value={searchText}
                                onChange={value => {
                                    setSearchText(value);
                                }}
                            />
                        </Col>
                        {/* <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                 <CityFilter
                                     value={stores.meStore.map.filters.city}
                                     onChange={value => {
                                         setCurrentPage(1);
                                         stores.meStore.map.filters.city = value;
                                         stores.meStore.orderPage.filters.regionGroups = [];
                                         stores.meStore.orderPage.filters.upperGroup = "all";
                                         stores.meStore.orderPage.filters.selectedSubCompanies = [];
                                     }}
                                 />
                             </Col> */}
                    </Row>
                </Col>
            </Row>

            {/* ================================================== */}
            {/* Orders Table */}
            <CustomerOrdersTable
                loading={orderListLoading}
                data={recallList?.rows || orderList.rows}
                dataCount={recallList?.dataCount || orderList.dataCount}
                orderType={filters.orderType}
                currentPage={currentPage}
                field={field}
                setField={setField}
                setPage={async page => {
                    setCurrentPage(page);
                    history.push("/musteri/siparislerim?page=" + page);
                }}
                setRecall={setRecall}
                recallRef={recallRef}
            />
        </CustomerFluidSubPageLayout>
    ));
};

export default CustomerOrdersPage;
